import { useState, useCallback, useEffect, useLayoutEffect, useRef, type RefObject } from 'react';
import {
    computePopoverOffset,
    resolvePopoverPlacement,
    type PopoverPlacement,
    type PlacementSide,
    type PopoverAlignment,
} from './popoverPositioning.ts';

const GAP = 8;
const TRIANGLE_HALF = 10;
const VIEWPORT_PAD = 8;
const FOCUSABLE_SELECTOR =
    'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

function focusPopover(pop: HTMLElement): void {
    const target = pop.querySelector<HTMLElement>(FOCUSABLE_SELECTOR);
    if (target) {
        target.focus();
    } else {
        if (!pop.hasAttribute('tabindex')) {
            pop.setAttribute('tabindex', '-1');
        }
        pop.focus();
    }
}

function setupLightDismiss(
    pop: HTMLElement,
    anchorEl: HTMLElement | null,
    onDismiss: () => void
): () => void {
    const handlePointerDown = (e: PointerEvent): void => {
        const target = e.target as Node;
        if (pop.contains(target) || anchorEl?.contains(target)) return;
        onDismiss();
        anchorEl?.focus();
    };
    const handleEscape = (e: KeyboardEvent): void => {
        if (e.key === 'Escape') {
            onDismiss();
            anchorEl?.focus();
        }
    };
    document.addEventListener('pointerdown', handlePointerDown, { capture: true, passive: true });
    document.addEventListener('keydown', handleEscape, { passive: true });
    return () => {
        document.removeEventListener('pointerdown', handlePointerDown, true);
        document.removeEventListener('keydown', handleEscape);
    };
}

/**
 * Viewport X/Y that current `style.left` / `style.top` (px) are measured from for this element's
 * border box. Recomputed each frame so `position: fixed` + `top`/`left` can target a desired
 * viewport rect despite margins, subpixel rounding, and ancestor transforms.
 */
function readFixedInsetViewportOrigin(el: HTMLElement): { x: number; y: number } | null {
    const top = parseFloat(el.style.top);
    const left = parseFloat(el.style.left);
    if (Number.isNaN(top) || Number.isNaN(left)) {
        return null;
    }
    const rect = el.getBoundingClientRect();
    return { x: rect.left - left, y: rect.top - top };
}

function computeTriangleOffset(
    side: PlacementSide,
    anchorWidth: number,
    anchorHeight: number,
    popoverWidth: number,
    popoverHeight: number,
    relLeft: number,
    relTop: number
): number {
    if (side === 'top' || side === 'bottom') {
        return Math.round(
            Math.min(
                Math.max(anchorWidth / 2 - relLeft, TRIANGLE_HALF),
                popoverWidth - TRIANGLE_HALF
            )
        );
    }
    return Math.round(
        Math.min(Math.max(anchorHeight / 2 - relTop, TRIANGLE_HALF), popoverHeight - TRIANGLE_HALF)
    );
}

function createPositioner(
    pop: HTMLElement,
    anchorEl: HTMLElement,
    config: {
        side: PlacementSide;
        align: PopoverAlignment;
    },
    callbacks: {
        onResolvedSide: (side: PlacementSide) => void;
        onTriangleOffset: (offset: number) => void;
    }
): { destroy: () => void } {
    pop.hidden = false;
    pop.style.top = '0px';
    pop.style.left = '0px';
    const originRect = pop.getBoundingClientRect();
    let originX = originRect.left;
    let originY = originRect.top;
    let lastPlacement: PopoverPlacement | null = null;

    const update = (): void => {
        const origin = readFixedInsetViewportOrigin(pop);
        if (origin) {
            originX = origin.x;
            originY = origin.y;
        }

        const anchorRect = anchorEl.getBoundingClientRect();
        const pw = pop.offsetWidth;
        const ph = pop.offsetHeight;
        const vw = window.innerWidth;
        const vh = window.innerHeight;

        const resolved = resolvePopoverPlacement(
            lastPlacement,
            config.side,
            config.align,
            anchorRect,
            pw,
            ph,
            vw,
            vh,
            GAP,
            VIEWPORT_PAD
        );

        const prev = lastPlacement;
        lastPlacement = resolved;

        if (!prev || prev.side !== resolved.side) {
            callbacks.onResolvedSide(resolved.side);
        }

        const { width: aw, height: ah } = anchorRect;
        const rel = computePopoverOffset(resolved.side, resolved.align, aw, ah, pw, ph, GAP);
        const newTop = `${Math.round(anchorRect.top + rel.top - originY)}px`;
        const newLeft = `${Math.round(anchorRect.left + rel.left - originX)}px`;
        if (pop.style.top !== newTop || pop.style.left !== newLeft) {
            pop.style.top = newTop;
            pop.style.left = newLeft;
        }

        callbacks.onTriangleOffset(
            computeTriangleOffset(resolved.side, aw, ah, pw, ph, rel.left, rel.top)
        );
    };

    update();

    let frameId = 0;
    const scheduleUpdate = (): void => {
        if (frameId === 0) {
            frameId = requestAnimationFrame(() => {
                frameId = 0;
                update();
            });
        }
    };

    document.addEventListener('scroll', scheduleUpdate, { capture: true, passive: true });
    window.addEventListener('resize', scheduleUpdate, { passive: true });
    window.visualViewport?.addEventListener('resize', scheduleUpdate, { passive: true });
    window.visualViewport?.addEventListener('scroll', scheduleUpdate, { passive: true });
    const resizeObserver = new ResizeObserver(scheduleUpdate);
    resizeObserver.observe(anchorEl);
    resizeObserver.observe(pop);

    return {
        destroy() {
            if (frameId !== 0) cancelAnimationFrame(frameId);
            document.removeEventListener('scroll', scheduleUpdate, true);
            window.removeEventListener('resize', scheduleUpdate);
            window.visualViewport?.removeEventListener('resize', scheduleUpdate);
            window.visualViewport?.removeEventListener('scroll', scheduleUpdate);
            resizeObserver.disconnect();
        },
    };
}

export function useAnchoredPopover({
    side,
    align,
    dismiss,
    open: isOpen,
    onOpenChange,
    anchorEl,
    autoFocus = true,
}: {
    side: PlacementSide;
    align: PopoverAlignment;
    dismiss: 'auto' | 'manual';
    open: boolean;
    onOpenChange: (open: boolean) => void;
    anchorEl: HTMLElement | null;
    autoFocus?: boolean;
}): {
    resolvedSide: PlacementSide;
    triangleOffset: number;
    popoverRef: RefObject<(HTMLDivElement & HTMLDialogElement) | null>;
} {
    const popoverRef = useRef<HTMLDivElement & HTMLDialogElement>(null);
    const [resolvedSide, setResolvedSide] = useState<PlacementSide>(side);
    const [triangleOffset, setTriangleOffset] = useState(0);

    useLayoutEffect(() => {
        if (!isOpen) {
            setResolvedSide(side);
        }
    }, [isOpen, side]);

    const onResolvedSide = useCallback((p: PlacementSide) => {
        setResolvedSide(prev => (prev === p ? prev : p));
    }, []);

    const onTriangleOffset = useCallback((offset: number) => {
        setTriangleOffset(prev => (prev === offset ? prev : offset));
    }, []);

    useEffect(() => {
        if (!isOpen || !autoFocus) return;
        const pop = popoverRef.current;
        if (pop) {
            focusPopover(pop);
        }
    }, [isOpen, autoFocus]);

    useEffect(() => {
        if (!isOpen || dismiss !== 'auto') {
            return undefined;
        }
        const pop = popoverRef.current;
        if (!pop) {
            return undefined;
        }
        return setupLightDismiss(pop, anchorEl, () => onOpenChange(false));
    }, [isOpen, dismiss, anchorEl, onOpenChange]);

    useLayoutEffect(() => {
        if (!isOpen) {
            return undefined;
        }
        const pop = popoverRef.current;
        if (!pop || !anchorEl) {
            return undefined;
        }
        const positioner = createPositioner(
            pop,
            anchorEl,
            { side, align },
            { onResolvedSide, onTriangleOffset }
        );
        return () => positioner.destroy();
    }, [isOpen, anchorEl, side, align, onResolvedSide, onTriangleOffset]);

    return {
        resolvedSide,
        triangleOffset,
        popoverRef,
    };
}
